<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>

        <template #main>
            <main-header title="Course Bundle Builder" icon="fa-layer-group" close="Back to Catalog" :close-route="{name: 'course-catalog'}"/>

            <div class="package-builder">

                <!-- Entry: choose your path -->
                <package-entry v-if="phase === 'entry'"
                               @start-self="goWizard(1)"
                               @start-rep="leadReturnStep = null; setPhase('lead');"/>

                <!-- Representative request -->
                <package-lead-form v-else-if="phase === 'lead'"
                                   @back="leadBack"
                                   @submitted="setPhase('leadDone')"/>

                <!-- Representative confirmation -->
                <div class="package-card notice" v-else-if="phase === 'leadDone'">
                    <div class="notice-icon is-success"><i class="fas fa-check"></i></div>
                    <div class="notice-title">Your representative will be in touch</div>
                    <div class="notice-text">We've received your request. A dedicated representative will reach out within one business day to help build your bundle and support your professional development going forward.</div>
                    <b-button type="is-primary" outlined class="mt-5" @click="goWizard(1)">Changed your mind? Build it yourself</b-button>
                </div>

                <!-- Wizard -->
                <div v-else-if="phase === 'wizard'">
                    <h2 class="wizard-heading has-text-white">Build Your Own Bundle</h2>
                    <package-steps :step="step" @go="goWizard"/>

                    <div class="columns wizard-columns">
                        <div class="column">
                            <step-needs v-if="step === 1" ref="activeStep" @back="setPhase('entry')" @continue="goWizard(2)"/>
                            <step-focus v-else-if="step === 2" :courses="courses" :loading="loadingCourses" @back="goWizard(1)" @continue="goWizard(3)"/>
                            <step-courses v-else-if="step === 3" :courses="courses" :loading="loadingCourses" @back="goWizard(2)" @continue="goWizard(4)"/>
                            <step-payment v-else-if="step === 4" @back="goWizard(3)" @continue="goWizard(5)"/>
                            <step-review v-else-if="step === 5" @back="goWizard(4)" @finish="finish"/>
                        </div>
                        <div class="column is-narrow side-column">
                            <package-summary :show-pay-options="step !== 5">
                                <b-button type="is-primary" expanded class="mt-4" :disabled="!sideCanContinue" @click="sideContinue">
                                    {{ sideLabel }}<i class="fas fa-arrow-right ml-2"></i>
                                </b-button>
                            </package-summary>
                            <div class="package-card mt-5">
                                <package-faq :faqs="stepFaqs"/>
                            </div>
                            <div class="package-card rep-bailout mt-5">
                                <div class="bailout-title"><i class="far fa-life-ring mr-2"></i>Feeling overwhelmed?</div>
                                <div class="bailout-text">You don't have to build this alone. A representative can size your bundle, choose courses with you, and handle enrollment and payment setup for you.</div>
                                <b-button type="is-primary" outlined expanded size="is-small" class="mt-3" @click="goLead">Work with a representative</b-button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pay-in-full checkout: card, terms and signature -->
                <package-checkout v-else-if="phase === 'checkout'"
                                  @back="goWizard(5)"
                                  @complete="setPhase('done')"/>

                <!-- Payment plan hand-off -->
                <div class="package-card notice" v-else-if="phase === 'handoff'">
                    <div class="notice-icon"><i class="fas fa-dollar-sign"></i></div>
                    <div class="notice-title">Finish setting up your payment plan</div>
                    <div class="notice-text">Monthly plans are managed by our lending partner, The Payment Option. Confirming saves your bundle and creates your plan — then sign your Deferred Payment Agreement to activate your courses.</div>
                    <div class="notice-summary">
                        <div class="summary-title">{{ pkgTargetHours }} Clock Hour Bundle</div>
                        <div class="summary-line">{{ pkgPlan.months }} payments of {{ pkgMonthly | money }}</div>
                        <div class="summary-line">First payment {{ firstPaymentDate }}</div>
                    </div>
                    <b-button type="is-primary" expanded size="is-medium" class="mt-5" :disabled="planProcessing" @click="confirmHandoff">
                        <i class="fas fa-spin fa-spinner mr-3" v-if="planProcessing"></i>Confirm &amp; Continue to Agreement<i class="fas fa-arrow-right ml-3" v-if="!planProcessing"></i>
                    </b-button>
                    <b-button type="is-text" expanded class="mt-2" :disabled="planProcessing" @click="goWizard(5)">Back to review</b-button>
                </div>

                <!-- Confirmation: plan purchases still need the TPO agreement
                     signed; paid purchases are ready to go -->
                <div class="package-card notice" v-else-if="phase === 'done' && planResult">
                    <div class="notice-icon is-success"><i class="fas fa-check"></i></div>
                    <div class="notice-title">Your bundle is saved — one step left</div>
                    <div class="notice-text">Sign your Deferred Payment Agreement with our lending partner, The Payment Option, to activate your courses. We've also sent this link to you by email and text.</div>
                    <div class="notice-summary">
                        <div class="summary-title">{{ pkgTargetHours }} Clock Hour Bundle</div>
                        <div class="summary-line">{{ pkgPlan.months }} payments of {{ pkgMonthly | money }}</div>
                        <div class="summary-line">First payment {{ planResult.defer_on }}</div>
                    </div>
                    <a :href="planResult.sign_url" target="_blank" rel="noopener">
                        <b-button type="is-primary" expanded size="is-medium" class="mt-5"><i class="fas fa-file-signature mr-3"></i>Sign Your Agreement</b-button>
                    </a>
                    <router-link :to="{name: 'enrollments'}">
                        <b-button type="is-text" expanded class="mt-2">Go to Course Enrollments</b-button>
                    </router-link>
                </div>
                <div class="package-card notice" v-else-if="phase === 'done'">
                    <div class="notice-icon is-success"><i class="fas fa-check"></i></div>
                    <div class="notice-title">You&rsquo;re enrolled!</div>
                    <div class="notice-text">Your {{ pkgTargetHours }} clock hour bundle is ready. Your courses now appear under Course Enrollments, and your hours are available to log right away.</div>
                    <router-link :to="{name: 'enrollments'}">
                        <b-button type="is-primary" expanded size="is-medium" class="mt-5">Go to Course Enrollments</b-button>
                    </router-link>
                </div>

            </div>
        </template>
    </framework>
</template>

<script>
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import MainHeader from "../../components/framework/MainHeader";
    import PackageEntry from "../../components/package/PackageEntry";
    import PackageLeadForm from "../../components/package/PackageLeadForm";
    import PackageSteps from "../../components/package/PackageSteps";
    import PackageSummary from "../../components/package/PackageSummary";
    import PackageFaq from "../../components/package/PackageFaq";
    import PackageCheckout from "../../components/package/PackageCheckout";
    import StepNeeds from "../../components/package/steps/StepNeeds";
    import StepFocus from "../../components/package/steps/StepFocus";
    import StepCourses from "../../components/package/steps/StepCourses";
    import StepPayment from "../../components/package/steps/StepPayment";
    import StepReview from "../../components/package/steps/StepReview";
    import {client as http} from "../../http_client";
    import {mapGetters, mapMutations} from "vuex";
    import {FAQ_BY_STEP, isPackageCourse, packageHourCap, COURSE_HOUR_OPTIONS, firstPaymentDateFor} from "@/constants/packages";

    const PHASES = ['entry', 'lead', 'leadDone', 'wizard', 'checkout', 'handoff', 'done'];

    export default {
        name: "PackageBuilder",
        components: {
            Framework, SidePanelNav, MainHeader,
            PackageEntry, PackageLeadForm, PackageSteps, PackageSummary, PackageFaq, PackageCheckout,
            StepNeeds, StepFocus, StepCourses, StepPayment, StepReview
        },
        data: function() {
            return {
                phase: 'entry',
                step: 1,
                // Set when the rep form is reached from inside the wizard, so
                // Back returns to the step they bailed from (session-local;
                // after a refresh Back falls back to the entry screen)
                leadReturnStep: null,
                // The interest ping fires once per page load, the first time
                // the user reaches the wizard (not the entry screen)
                startedPinged: false,
                planProcessing: false,
                planResult: null,
                courses: [],
                loadingCourses: true
            };
        },
        computed: {
            ...mapGetters(['pkgTargetHours', 'pkgRemainingHours', 'pkgFocusCategories', 'pkgPay', 'pkgPlan', 'pkgMonthly']),
            // First payment is always October 1 — the raise date (same math
            // as the review step and the server's purchase-plan endpoint)
            firstPaymentDate() {
                return firstPaymentDateFor(this.pkgTargetHours).format('MMMM D, YYYY');
            },
            stepFaqs() {
                return FAQ_BY_STEP[this.step] || [];
            },
            // The sidebar continue mirrors each step's own gating
            sideCanContinue() {
                switch (this.step) {
                    case 1: return this.pkgTargetHours > 0;
                    case 2: return this.pkgFocusCategories.length > 0;
                    case 3: return this.pkgTargetHours > 0 && this.pkgRemainingHours === 0;
                    case 4: return !!this.pkgPay;
                    default: return true;
                }
            },
            sideLabel() {
                switch (this.step) {
                    case 1: return 'Continue: Your Focus';
                    case 2: return 'Continue to Courses';
                    case 3: return 'Continue to Payment';
                    case 4: return 'Review Your Bundle';
                    default: return this.pkgPay === 'plan' ? 'Continue to Plan Setup' : 'Continue to Checkout';
                }
            }
        },
        methods: {
            ...mapMutations(['resetPkg', 'setPkgHours']),
            // Navigation writes the URL; syncFromRoute() reads it back into
            // state, so browser back/forward and refresh keep position
            setPhase(phase) {
                const query = phase === 'entry' ? {} : {phase};
                this.$router.push({query}).catch(() => {});
            },
            goWizard(step) {
                this.$router.push({query: {phase: 'wizard', step: String(step)}}).catch(() => {});
            },
            // The sidebar "Feeling overwhelmed?" bail-out: hand off to a
            // representative from any wizard step; selections stay in the
            // store in case they come back
            goLead() {
                this.leadReturnStep = this.step;
                this.setPhase('lead');
            },
            leadBack() {
                if (this.leadReturnStep) {
                    this.goWizard(this.leadReturnStep);
                } else {
                    this.setPhase('entry');
                }
            },
            syncFromRoute() {
                const q = this.$route.query;
                let phase = PHASES.includes(q.phase) ? q.phase : 'entry';
                let step = Math.min(5, Math.max(1, parseInt(q.step) || 1));

                // Deep links can preselect the hours (the dashboard's
                // professional-development section sizes a bundle to the
                // student's salary-column gap); the step still validates it
                const hours = parseInt(q.hours, 10);
                if (hours > 0 && hours !== this.pkgTargetHours) this.setPkgHours(hours);

                // A refresh drops nothing (the store survives in-session), but a
                // deep link past step 1 without chosen hours has nothing to
                // build on — restart the wizard
                if (phase === 'wizard' && step > 1 && !this.pkgTargetHours) step = 1;
                if ((phase === 'checkout' || phase === 'handoff' || phase === 'done') && !this.pkgTargetHours) phase = 'entry';

                this.phase = phase;
                this.step = step;

                // Tells the API someone started building a bundle so we can
                // gauge interest in the feature; the alerting itself is a
                // server-side code hook. Landing on the entry screen or the
                // rep form doesn't count — reaching step 1 does.
                if (phase === 'wizard' && !this.startedPinged) {
                    this.startedPinged = true;
                    http.post('app/packages/started', {phase, step}).catch(() => {});
                }
            },
            finish() {
                // Full payment goes through the card checkout (terms +
                // signature); plans hand off to the payment partner.
                if (this.pkgPay === 'plan') {
                    this.setPhase('handoff');
                } else {
                    this.setPhase('checkout');
                }
            },
            // Deferred Payment Agreement: create the TPO plan + the locked
            // enrollments server-side, then lead the student to the
            // sign-agreement URL (also delivered by email + SMS)
            confirmHandoff() {
                if (this.planProcessing) return;
                this.planProcessing = true;
                http.post('app/packages/purchase-plan', {
                    hours: this.pkgTargetHours,
                    split_upfront: this.$store.getters.pkgSplitUpfront,
                    selections: this.$store.getters.pkgSelections.map(s => ({course_id: s.id, name: s.name, hours: s.hours}))
                }).then(response => {
                    this.planProcessing = false;
                    this.planResult = response.data;
                    this.$store.dispatch('getEnrollments');
                    this.setPhase('done');
                }).catch(err => {
                    console.error(err);
                    this.planProcessing = false;
                    const serverMsg = err.response && err.response.data && err.response.data.error;
                    this.$notify('error', 'Could Not Set Up Your Plan', serverMsg || 'Something went wrong creating your payment plan. Please try again, or contact support@my247ed.com.', {duration: 8000, permanent: false});
                });
            },
            sideContinue() {
                if (!this.sideCanContinue) return;
                // Step 1 saves the profile answers on its way out — route
                // through the step component rather than skipping past it
                if (this.step === 1 && this.$refs.activeStep) {
                    this.$refs.activeStep.next();
                } else if (this.step === 5) {
                    this.finish();
                } else {
                    this.goWizard(this.step + 1);
                }
            },
            loadCourses() {
                // Same source as the catalog, narrowed to the package
                // certification's courses that can take at least the smallest
                // allocation (some legacy configs cap below 10 hours)
                http.get('app/courses/new').then(response => {
                    this.courses = response.data
                        .filter(course => isPackageCourse(course) && packageHourCap(course) >= COURSE_HOUR_OPTIONS[0])
                        .sort((a, b) => (a.title.toUpperCase() > b.title.toUpperCase()) ? 1 : -1);
                    this.loadingCourses = false;
                }).catch(() => {
                    this.loadingCourses = false;
                });
            }
        },
        watch: {
            '$route.query'() {
                this.syncFromRoute();
            }
        },
        mounted() {
            this.syncFromRoute();
            this.loadCourses();
            // Rate-only fetch; the included tax is derived from the live price
            http.get('app/users/sales-tax', {params: {amount: 100}}).then(response => {
                this.$store.commit('setPkgTax', {rate: response.data.taxRate, jurisdiction: response.data.jurisdiction});
            }).catch(() => {});
        }
    };
</script>

<style lang="scss">
    /* Shared building blocks for the package flow (unscoped on purpose:
       the step components under components/package/ use these too) */
    .package-builder {
        padding-bottom: 4rem;

        .wizard-heading {
            font-size: 1.6rem;
            font-weight: 900;
            // Same top rhythm as the app's other screens (content mt-6);
            // the steps bar below carries its own 1.5rem gap
            margin: 3rem 0 0;
        }

        .wizard-columns {
            align-items: flex-start;

            .side-column {
                width: 344px;

                @media (max-width: 1023px) {
                    width: 100%;
                }

                position: sticky;
                top: 1.5rem;
            }
        }

        .package-card {
            background-color: white;
            border-radius: 4px;
            box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.4);
            padding: 1.7rem 2rem;
        }

        .step-heading {
            font-size: 1.25rem;
            font-weight: 900;
            color: #33475b;
        }

        .step-sub {
            font-size: 0.9rem;
            color: #666d7a;
            margin-top: 0.4rem;
            max-width: 90%;
        }

        .step-actions {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-top: 1.6rem;
        }

        .chips {
            display: flex;
            flex-wrap: wrap;
            gap: 8px;

            .chip {
                font-family: inherit;
                font-size: 0.85rem;
                font-weight: bold;
                padding: 8px 14px;
                border-radius: 999px;
                cursor: pointer;
                border: 1px solid #c4cfda;
                background: #ffffff;
                color: #33475b;

                &:hover {
                    border-color: #7fa8cd;
                }

                &.is-selected {
                    border-color: #2e5f8f;
                    background: #2e5f8f;
                    color: #ffffff;
                }
            }
        }

        .rep-bailout {
            background: #f2f7fc;

            .bailout-title {
                font-size: 0.95rem;
                font-weight: 900;
                color: #33475b;

                i {
                    color: #366ea5;
                }
            }

            .bailout-text {
                font-size: 0.85rem;
                color: #666d7a;
                margin-top: 0.4rem;
            }
        }

        .notice {
            max-width: 560px;
            margin: 3rem auto 0;
            text-align: center;
            padding: 2.2rem 2.5rem;

            .notice-icon {
                width: 56px;
                height: 56px;
                border-radius: 50%;
                background: #f2f7fc;
                border: 2px solid #366ea5;
                margin: 0 auto;
                display: flex;
                align-items: center;
                justify-content: center;

                i {
                    font-size: 1.4rem;
                    color: #366ea5;
                }

                &.is-success {
                    background: #e7f4ec;
                    border-color: #2e7d4f;

                    i {
                        color: #2e7d4f;
                    }
                }
            }

            .notice-title {
                font-size: 1.3rem;
                font-weight: 900;
                color: #33475b;
                margin-top: 1.1rem;
            }

            .notice-text {
                font-size: 0.9rem;
                color: #666d7a;
                margin-top: 0.6rem;
            }

            .notice-summary {
                margin-top: 1.1rem;
                background: #f2f7fc;
                border-radius: 6px;
                padding: 0.9rem;
                font-size: 0.9rem;
                color: #33475b;

                .summary-title {
                    font-weight: 900;
                }

                .summary-line {
                    color: #666d7a;
                    margin-top: 2px;
                }
            }
        }
    }
</style>
